import NOTES_SPELLINGS from '../data/notes.json' with { type: 'json' }; // Corrected import name for clarity
// This is the primary NOTES array that other parts of the system might use.
// It should be a simple string array, e.g., ["C", "C#", "D", "D#", ...].
// For the purpose of this utility, we'll re-import it from chordUtils if that's where it's canonical,
// or ensure notes.json is string[] and chordUtils imports that.
// For now, assuming notes.json IS the string[]:
const DEFAULT_NOTES_ARRAY = NOTES_SPELLINGS;
// Internal mapping for enharmonic equivalents.
// Provides the alternative for a given default note name if one exists.
const enharmonicEquivalents = {
    "C♯": "D♭", "D♭": "C♯",
    "D♯": "E♭", "E♭": "D♯",
    "F♯": "G♭", "G♭": "F♯",
    "G♯": "A♭", "A♭": "G♯",
    "A♯": "B♭", "B♭": "A♯",
    "E": "F♭", "F♭": "E",
    "B": "C♭", "C♭": "B",
    "C": "B♯", "B♯": "C",
    "F": "E♯", "E♯": "F",
};
// The four uncommon enharmonic spellings mapped to their common equivalents.
// These spellings are not present in the chromatic scales / notes.json primary
// spellings, so note-math helpers normalize them before computing.
const uncommonSpellingEquivalents = {
    "B♯": "C", "E♯": "F", "C♭": "B", "F♭": "E",
};
/**
 * Normalizes the uncommon enharmonic spellings (B♯, E♯, C♭, F♭) to their
 * common equivalents (C, F, B, E). All other note names pass through
 * unchanged, preserving the user's sharp/flat preference.
 */
export const normalizeUncommonSpelling = (noteName) => uncommonSpellingEquivalents[noteName] ?? noteName;
/**
 * Normalizes ASCII accidentals in a note name to Unicode (F# → F♯, Bb → B♭).
 * Note names already using Unicode accidentals (or naturals) pass through
 * unchanged. Double accidentals are normalized symbol-by-symbol.
 */
export const normalizeAccidentalSymbol = (noteName) => {
    if (noteName.length > 1) {
        const base = noteName.charAt(0);
        const accidental = noteName.substring(1);
        if (accidental === '#')
            return `${base}♯`;
        if (accidental === 'b')
            return `${base}♭`;
        if (accidental === '##')
            return `${base}♯♯`;
        if (accidental === 'bb')
            return `${base}♭♭`;
    }
    return noteName;
};
/**
 * Normalizes an ASCII alteration symbol to Unicode (b5 → ♭5, #9 → ♯9).
 * Unicode alterations and non-alteration values ("alt", "-") pass through
 * unchanged. This is the canonical vocabulary used by chord-alterations.json,
 * parseChordString, and getChordNotes.
 */
export const normalizeAlterationSymbol = (alteration) => {
    if (/^b\d+$/.test(alteration))
        return `♭${alteration.substring(1)}`;
    if (/^#\d+$/.test(alteration))
        return `♯${alteration.substring(1)}`;
    return alteration;
};
// Helper to check if a note is sharp (now checks for ♯, but can also accept # for robustness if needed elsewhere)
const isSharp = (noteName) => noteName.includes('♯');
// Helper to check if a note is flat (now checks for ♭, but can also accept b for robustness if needed elsewhere)
const isFlat = (noteName) => noteName.includes('♭');
// Helper to get the base note name (without accidentals)
export const getBaseNote = (noteName) => noteName.charAt(0); // Export if needed by chordUtils
/**
 * Calculates the expected letter name (A-G) for a given interval role from a root letter.
 * E.g., if rootLetter is 'C' and intervalRole is 3 (a third), returns 'E'.
 * @param rootLetter The letter name of the root (e.g., 'C', 'F', 'B').
 * @param intervalRole The role of the interval (e.g., 1 for unison/root, 3 for third, 5 for fifth).
 * @returns The expected letter name.
 */
export const getExpectedLetterForIntervalRole = (rootLetter, intervalRole) => {
    const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
    const rootLetterNormalized = getBaseNote(rootLetter).toUpperCase(); // Ensure it's just the letter
    const rootIndexInAlphabet = letters.indexOf(rootLetterNormalized);
    if (rootIndexInAlphabet === -1) {
        return '?'; // Should not happen with valid root note
    }
    // intervalRole is 1-based (1st, 2nd, 3rd...). We need 0-based for array indexing.
    // (intervalRole - 1) gives the number of steps up in the letter sequence.
    const targetLetterIndex = (rootIndexInAlphabet + intervalRole - 1) % 7;
    return letters[targetLetterIndex];
};
/**
 * Selects the most appropriate enharmonic spelling for a note given its context.
 *
 * @param targetNoteIndex The semitone index (0-11) of the note to be named.
 * @param rootNoteName The name of the root note of the chord (e.g., "C", "F#", "Bb").
 * @param chordQuality The quality string (e.g., "diminished7", "major", "minor7").
 * @param existingNotesInChord An array of note names already determined for the chord,
 *                             to help guide spelling consistency.
 * @returns The best enharmonically spelled note name.
 */
export const getEnharmonicallyCorrectNoteName = (targetNoteIndex, rootNoteName, // Used for root accidental preference as a fallback
_chordQuality, // Keep for potential future use, but primary logic uses targetLetter
existingNotesInChord, targetLetter // New parameter
) => {
    const defaultNoteName = DEFAULT_NOTES_ARRAY[targetNoteIndex];
    if (!defaultNoteName) {
        return '?'; // Should not happen
    }
    const alternativeName = enharmonicEquivalents[defaultNoteName];
    const possibleNames = alternativeName ? [defaultNoteName, alternativeName] : [defaultNoteName];
    if (possibleNames.length === 1) {
        return possibleNames[0];
    }
    // Rule 1: Prioritize the spelling that matches the targetLetter.
    const nameMatchingTargetLetter = possibleNames.find(name => getBaseNote(name) === targetLetter);
    if (nameMatchingTargetLetter) {
        // Special case for diminished 7th's 7th degree (enharmonically a M6)
        // If the targetLetter match results in a double-flat/sharp, and a simpler alternative exists
        // (which might not match the targetLetter but is simpler), prefer the simpler one.
        // This handles cases like Cdim7: C-Eb-Gb-A (where target letter for A was B, leading to Bbb).
        const isDiminishedSeventhInterval = existingNotesInChord.length === 3 && _chordQuality === 'diminished7';
        if (isDiminishedSeventhInterval && (nameMatchingTargetLetter.includes('bb') || nameMatchingTargetLetter.includes('##'))) {
            const simplerAlternative = possibleNames.find(name => name !== nameMatchingTargetLetter);
            if (simplerAlternative)
                return simplerAlternative;
        }
        return nameMatchingTargetLetter;
    }
    // Fallback if no name matches the targetLetter (should be rare if targetLetter is derived correctly
    // and notes.json covers standard spellings for the semitone index).
    // This might happen if, for example, targetLetter is 'E' for index 3 (D#/Eb), and we only had "D#" in possibleNames.
    // Rule 2: Avoid using the same letter name as an *existing note in the chord* (excluding root itself, which is handled by targetLetter).
    // This is a secondary check if targetLetter matching wasn't conclusive or applicable.
    const nonRootExistingBaseNotes = existingNotesInChord.slice(1).map(getBaseNote); // Exclude root
    if (possibleNames.length > 1) {
        const nameAvoidingClash = possibleNames.find(name => !nonRootExistingBaseNotes.includes(getBaseNote(name)));
        if (nameAvoidingClash) {
            const otherName = possibleNames.find(name => name !== nameAvoidingClash);
            if (otherName && nonRootExistingBaseNotes.includes(getBaseNote(otherName))) {
                return nameAvoidingClash; // Prefer the one that doesn't clash if the other does
            }
            // If both or neither clash with non-root notes, this rule isn't decisive yet among them.
            // `nameAvoidingClash` would be the first one found that doesn't clash.
        }
    }
    // Rule 3: Root Accidental Preference
    // If the root is flat, prefer a flat spelling. If sharp, prefer sharp.
    if (isFlat(rootNoteName)) {
        const flatName = possibleNames.find(isFlat);
        if (flatName)
            return flatName;
    }
    if (isSharp(rootNoteName)) {
        const sharpName = possibleNames.find(isSharp);
        if (sharpName)
            return sharpName;
    }
    // Rule 4: For natural roots, if a choice exists (e.g., D#/Eb), generally prefer flat for minor-ish/diminished-ish intervals
    // and sharp for major-ish/augmented-ish if that's the default.
    // This is hard to generalize without knowing the interval quality.
    // The targetLetter rule should handle most cases.
    // A simple preference for flats over sharps for natural roots if no other rule applied:
    if (!isSharp(rootNoteName) && !isFlat(rootNoteName)) {
        const flatName = possibleNames.find(isFlat);
        if (flatName && possibleNames.includes(enharmonicEquivalents[flatName] || '')) { // Check if its sharp equivalent was also an option
            return flatName;
        }
    }
    // Final Fallback: Return the default note name from notes.json (usually the sharp version or unambiguous natural)
    return defaultNoteName;
};
/**
 * Finds the semitone index (0-11) for a given note name,
 * considering its primary spelling in DEFAULT_NOTES_ARRAY or its enharmonic equivalent.
 * Accepts both ASCII (F#, Bb) and Unicode (F♯, B♭) accidentals.
 * @param noteName The note name (e.g., "C#", "Db", "E").
 * @returns The semitone index, or -1 if not found.
 */
export const getNoteIndex = (noteName) => {
    const normalized = normalizeAccidentalSymbol(noteName);
    let index = DEFAULT_NOTES_ARRAY.indexOf(normalized);
    if (index !== -1) {
        return index;
    }
    // If not found, check if the enharmonic equivalent's primary spelling is in the array
    // (e.g., if noteName is "D♭", enharmonicEquivalents["D♭"] is "C♯", then find index of "C♯")
    const primarySpellingAttempt = enharmonicEquivalents[normalized];
    if (primarySpellingAttempt) {
        index = DEFAULT_NOTES_ARRAY.indexOf(primarySpellingAttempt);
        if (index !== -1) {
            return index;
        }
    }
    return -1; // Note name or its primary enharmonic equivalent not found
};
/**
 * Returns an array of possible spellings for a given note name.
 * If the note has an enharmonic equivalent, it returns both spellings.
 * Otherwise, it returns an array containing only the original note name.
 * The primary spelling (from DEFAULT_NOTES_ARRAY or the input itself if not accidental) is listed first.
 * @param noteName The note name (e.g., "C#", "Db", "E").
 * @returns An array of string spellings.
 */
export const getEnharmonicAlternatives = (noteName) => {
    if (!noteName)
        return [];
    const equivalent = enharmonicEquivalents[noteName];
    const primarySpelling = DEFAULT_NOTES_ARRAY.find(n => n === noteName || (equivalent !== undefined && equivalent === n));
    if (primarySpelling && equivalent && primarySpelling !== equivalent) {
        // Ensure the "default" or "sharper" or "more common" one from DEFAULT_NOTES_ARRAY comes first if noteName is the alternative
        // This helps if the input is 'Db', it should still be ['C#', 'Db'] if 'C#' is the primary in DEFAULT_NOTES_ARRAY
        // Find which of noteName or its equivalent is in DEFAULT_NOTES_ARRAY to decide order.
        if (DEFAULT_NOTES_ARRAY.includes(noteName) && DEFAULT_NOTES_ARRAY.indexOf(noteName) < (DEFAULT_NOTES_ARRAY.indexOf(equivalent) !== -1 ? DEFAULT_NOTES_ARRAY.indexOf(equivalent) : Infinity)) {
            return [noteName, equivalent];
        }
        else if (DEFAULT_NOTES_ARRAY.includes(equivalent)) {
            return [equivalent, noteName];
        }
        // Fallback if neither specific version is in DEFAULT_NOTES_ARRAY (should not happen with current setup)
        return [noteName, equivalent].sort(); // Simple sort as a fallback
    }
    else if (equivalent) {
        // This case handles if noteName itself is not in DEFAULT_NOTES_ARRAY but its equivalent is.
        // Example: noteName = "E##", equivalent = "F#" (if F# is in DEFAULT_NOTES_ARRAY)
        // Or if noteName is "Db" and "C#" is in DEFAULT_NOTES_ARRAY
        const defaultForEquivalent = DEFAULT_NOTES_ARRAY.find(n => n === equivalent);
        if (defaultForEquivalent) {
            // We want the one in DEFAULT_NOTES_ARRAY first.
            return [defaultForEquivalent, noteName].sort((a, b) => {
                const aIsDefault = DEFAULT_NOTES_ARRAY.includes(a);
                const bIsDefault = DEFAULT_NOTES_ARRAY.includes(b);
                if (aIsDefault && !bIsDefault)
                    return -1;
                if (!aIsDefault && bIsDefault)
                    return 1;
                if (a.endsWith("#") && b.endsWith("b"))
                    return -1; // Prefer sharps first in ambiguous sort
                if (a.endsWith("b") && b.endsWith("#"))
                    return 1;
                return a.localeCompare(b);
            });
        }
        return [noteName, equivalent].sort();
    }
    return [noteName];
};
